/**
 * QboTaxesTab — codes de taxes QuickBooks du client (TPS / TVQ : taux de vente et d'achat par agence) et agences de taxes ;
 * lien vers le sommaire des taxes par agence (rapport lu en direct).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Loader2, Percent } from "lucide-react";
import { api } from "@/api";
import { Btn, Empty, Pill } from "../../comptabilite/_shared";
import { Card, TH, THead, Table } from "../../comptabilite/reports/_bits";
import { pct, qboBase } from "./_qbo";

const Rates = ({ rates }) => rates.length === 0 ? <span className="text-slate-400">—</span> : (
  <div className="space-y-0.5">{rates.map((r, i) => <div key={i} className="text-xs"><span className="font-mono font-semibold">{pct(r.rate)}</span> <span className="text-slate-700">{r.name}</span>{r.agency && <span className="text-slate-400"> · {r.agency}</span>}</div>)}</div>
);

export default function QboTaxesTab({ masterId, summary }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [inactive, setInactive] = useState(false);
  useEffect(() => { api.get(`${qboBase(masterId)}/entities/taxes`, { params: { include_inactive: inactive || undefined } }).then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, [masterId, inactive]);
  const agencies = summary?.tax_agencies || [];
  return (
    <div className="space-y-4" data-testid="qbo-taxes-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} data-testid="qbo-taxes-inactive" /> Inclure les codes inactifs</label>
        <Btn tone="sky" icon={BarChart3} className="ml-auto" onClick={() => navigate(`/admin/tenue-livres/${masterId}/quickbooks?tab=rapports&report=tax-summary`)} testId="qbo-taxes-report-btn">Sommaire des taxes (TPS / TVQ)</Btn>
      </div>
      {agencies.length > 0 && (
        <Card title="Agences de taxes" testId="qbo-tax-agencies">
          <div className="px-4 py-2 flex flex-wrap gap-2 text-xs">
            {agencies.map((a) => <Pill key={a.id} cls="bg-lime-50 text-lime-900 border-lime-200">{a.name}{a.registration ? ` · ${a.registration}` : ""}</Pill>)}
          </div>
        </Card>
      )}
      <Card title="Codes de taxes" right={data ? `${data.items.length} code(s)` : ""} testId="qbo-tax-codes">
        {!data ? <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          : data.items.length === 0 ? <Empty icon={Percent} text="Aucun code de taxe dans la copie locale — cliquez « Actualiser » en haut." testId="qbo-taxes-empty" />
            : (
              <Table>
                <THead><TH className="w-40">Code</TH><TH>Description</TH><TH>Ventes</TH><TH>Achats</TH><TH right className="w-24">Total vente</TH><TH className="w-20"></TH></THead>
                <tbody className="divide-y">
                  {data.items.map((c) => (
                    <tr key={c.qbo_id} className="hover:bg-emerald-50/40 align-top" data-testid={`qbo-tax-code-${c.qbo_id}`}>
                      <td className="px-3 py-2 font-semibold text-slate-800">{c.name}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{c.description}</td>
                      <td className="px-3 py-2"><Rates rates={c.sales_rates} /></td>
                      <td className="px-3 py-2"><Rates rates={c.purchase_rates} /></td>
                      <td className="px-3 py-2 text-right font-mono text-sm">{c.taxable ? pct(c.sales_total) : "—"}</td>
                      <td className="px-3 py-2">{!c.active && <Pill cls="bg-slate-200 text-slate-600 border-slate-300">Inactif</Pill>}{c.active && !c.taxable && <Pill>Exonéré</Pill>}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
      </Card>
    </div>
  );
}
